import type { TFunction } from 'i18next';

/**
 * Human labels for OAuth scopes.
 *
 * Deliberately client-side: estuary sends raw scope keys (`persons:read`) and
 * the wording is translated here, so copy changes and new languages don't
 * require an API release. An unrecognized key still renders — falling back to
 * the raw string is better than silently hiding a permission the user is about
 * to grant.
 */

export type TScopeKind = 'read' | 'write';

export interface IScopeDisplay {
  key: string;
  label: string;
  /** Writes are called out separately — granting mutation is a different decision. */
  kind: TScopeKind;
}

export function describeScope(key: string, t: TFunction): IScopeDisplay {
  // Only a known read is shown as one: an unrecognized key may mutate, so it reads as the heavier grant.
  const kind: TScopeKind = key.endsWith(':read') ? 'read' : 'write';
  const labels: Record<string, string> = {
    'persons:read': t('Read people and their details'),
    'persons:write': t('Create and update people'),
    'tags:read': t('Read tags'),
    'tags:write': t('Create, update, and delete tags'),
    'custom_fields:read': t('Read custom fields'),
    'custom_fields:write': t('Create and update custom fields'),
    'breadcrumbs:read': t('Read activity events'),
    'breadcrumbs:write': t('Record activity events'),
    'campaigns:read': t('Read campaigns'),
    'projects:read': t('Read projects'),
    'projects:write': t('Create and update projects'),
    'feed:read': t("Read your account's activity feed"),
  };
  return { key, label: labels[key] ?? key, kind };
}

/** Writes first: the weightiest permissions should be read before the rest. */
export function describeScopes(keys: string[], t: TFunction): IScopeDisplay[] {
  return keys
    .map((key) => describeScope(key, t))
    .sort((a, b) => {
      if (a.kind !== b.kind) {
        return a.kind === 'write' ? -1 : 1;
      }
      return a.label.localeCompare(b.label);
    });
}
